import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

export default class extends Controller {
  static targets = [
    "rulesContainer",
    "ruleTemplate",
    "filtersContainer",
    "filterTemplate",
    "matchedPlansFrame",
    "ruleItem",
    "spinnerTemplate",
    "unmatchedPlansFrame",
    "wildcardInfo",
  ];

  static values = {
    ruleIndex: { type: Number, default: 0 },
    filterWildcardSupportOperators: { type: Array, default: [] },
    filterOperatorPlaceholders: { type: Object, default: {} },
    planTypeId: { type: Number, default: null },
    planTypeName: { type: String, default: "" },
    planTypeRules: { type: Array, default: [] },
  };

  connect() {
    this.updateUnmatchedPlansTimeout = null;
  }

  disconnect() {
    if (this.updateUnmatchedPlansTimeout) {
      clearTimeout(this.updateUnmatchedPlansTimeout);
    }
  }

  changePlanTypeName(event) {
    this.planTypeNameValue = event.target.value;
  }

  addRule(event) {
    event.preventDefault();

    const template = this.ruleTemplateTarget.content.cloneNode(true);
    const ruleElement = template.querySelector(".rule-item");

    ruleElement.innerHTML = ruleElement.innerHTML.replace(/NEW_RULE/g, this.ruleIndexValue);
    ruleElement.dataset.ruleIndex = this.ruleIndexValue;

    // The UI Virtual ID is used to uniquely identify
    // a newly added rule (not persisted in the DB yet) in the DOM.
    // It is used to update a turbo frame by that ID.
    const ruleRandomUiVirtualId = this.#generateRandomString();
    ruleElement.setAttribute("id", `rule-item-${ruleRandomUiVirtualId}`);
    ruleElement.dataset.uiVirtualId = ruleRandomUiVirtualId;

    const matchedPlansTurboFrame = template.querySelector(".matched-plans-turbo-frame");
    if (matchedPlansTurboFrame) {
      matchedPlansTurboFrame.setAttribute("id", `matched-plans-${ruleRandomUiVirtualId}`);
    }

    this.rulesContainerTarget.appendChild(ruleElement);

    this.ruleIndexValue++;
  }

  removeRule(event) {
    event.preventDefault();

    const ruleItem = event.target.closest(".rule-item");
    const destroyField = ruleItem.querySelector("input[name*='[_destroy]']");

    if (destroyField) {
      if (ruleItem.querySelector("input[name*='[id]']").value) {
        // Rule exists in database - mark for destruction
        destroyField.value = "1";
        ruleItem.style.display = "none";
      } else {
        // Rule not yet saved - remove from DOM
        ruleItem.remove();
      }
    }
    this.#updatePlanTypeRulesJsonAndPlanLists();
  }

  addFilter(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const ruleIndex = button.dataset.ruleIndex;
    const ruleItem = button.closest(".rule-item");
    const filtersContainer = ruleItem.querySelector(
      `[data-admin--membership-plan-type-form-target="filtersContainer"][data-rule-index="${ruleIndex}"]`
    );
    const filterTemplate = ruleItem.querySelector(
      `[data-admin--membership-plan-type-form-target="filterTemplate"][data-rule-index="${ruleIndex}"]`
    );

    if (!filtersContainer || !filterTemplate) {
      console.error("Could not find filters container or template for rule", ruleIndex);
      return;
    }

    const currentFilters = filtersContainer.querySelectorAll(
      ".filter-item:not([style*='display: none'])"
    );
    const filterIndex = currentFilters.length;

    const template = filterTemplate.content.cloneNode(true);
    const filterElement = template.querySelector(".filter-item");

    // Replace placeholder with actual index
    filterElement.innerHTML = filterElement.innerHTML.replace(
      new RegExp(`NEW_FILTER_${ruleIndex}`, "g"),
      filterIndex
    );
    filterElement.dataset.filterIndex = filterIndex;

    filtersContainer.appendChild(filterElement);
  }

  removeFilter(event) {
    event.preventDefault();

    const filterItem = event.target.closest(".filter-item");
    const destroyField = filterItem.querySelector("input[name*='[_destroy]']");

    if (destroyField) {
      if (filterItem.querySelector("input[name*='[id]']").value) {
        // Filter exists in database - mark for destruction
        destroyField.value = "1";
        filterItem.style.display = "none";
      } else {
        // Filter not yet saved - remove from DOM
        filterItem.remove();
      }
    }
    this.#updatePlanTypeRulesJsonAndPlanLists();
  }

  changeOperator(event) {
    const select = event.target;
    const operator = select.value;
    const filterItem = select.closest(".filter-item");

    if (!filterItem) return;

    const valueInput = filterItem.querySelector(".value-input");
    if (valueInput && this.filterOperatorPlaceholdersValue[operator]) {
      valueInput.placeholder = this.filterOperatorPlaceholdersValue[operator];
    }

    // Show/hide wildcard info based on operator
    const wildcardInfo = filterItem.querySelector(".wildcard-info");
    if (wildcardInfo) {
      const supportsWildcards = this.filterWildcardSupportOperatorsValue.includes(operator);
      if (supportsWildcards) {
        wildcardInfo.classList.remove("hidden");
      } else {
        wildcardInfo.classList.add("hidden");
      }
    }

    this.#updatePlanTypeRulesJsonAndPlanLists();
  }

  handleInputChange(_event) {
    this.#updatePlanTypeRulesJsonAndPlanLists();
  }

  #updatePlanTypeRulesJsonAndPlanLists() {
    const rules = this.#prepareRulesPayload();

    if (JSON.stringify(rules) === JSON.stringify(this.planTypeRulesValue)) {
      return;
    }

    this.planTypeRulesValue = rules;

    // Skip plans fetching if no non-destroyed rules are present
    if (rules.length === 0 || rules.every((rule) => rule._destroy)) {
      return;
    }

    this.#debouncePlansFetching();
  }

  #prepareRulesPayload() {
    const rules = [];
    const ruleItems = this.ruleItemTargets;

    ruleItems.forEach((ruleItem) => {
      const ruleIdInput = ruleItem.querySelector("input[name*='[id]']");
      const rulePriorityInput = ruleItem.querySelector("input[name*='[priority]']");
      const ruleDestroyInput = ruleItem.querySelector("input[name*='[_destroy]']");

      const ruleIdValue = parseInt(ruleIdInput.value, 10) || null;
      const rulePriorityValue = parseInt(rulePriorityInput.value, 10) || null;
      const ruleDestroyValue = ruleDestroyInput ? ruleDestroyInput.value === "1" : false;
      const ruleUiVirtualId = ruleItem.dataset.uiVirtualId;

      if (ruleDestroyValue && ruleIdValue) {
        // Include destroyed existing rules with _destroy: true
        rules.push({
          id: ruleIdValue,
          priority: rulePriorityValue,
          plan_type_id: this.planTypeIdValue,
          plan_type_name: this.planTypeNameValue,
          ui_virtual_id: ruleUiVirtualId,
          _destroy: true,
          plan_type_rule_filters: [],
        });

        return;
      }

      // Skip if no priority is set
      if (rulePriorityValue === null) {
        return;
      }

      const rule = {
        id: ruleIdValue,
        priority: rulePriorityValue,
        plan_type_id: this.planTypeIdValue,
        plan_type_name: this.planTypeNameValue,
        ui_virtual_id: ruleUiVirtualId,
        _destroy: ruleDestroyValue,
        plan_type_rule_filters: [],
      };

      const filterItems = ruleItem.querySelectorAll(".filter-item");

      filterItems.forEach((filterItem) => {
        const filterIdInput = filterItem.querySelector("input[name*='[id]']");
        const filterOperatorInput = filterItem.querySelector("input[name*='[operator]']");
        const filterValueInput = filterItem.querySelector("input[name*='[value]']");
        const filterCaseSensitiveInput = filterItem.querySelector(
          "input[type='checkbox'][name*='[case_sensitive]']"
        );
        const filterDestroyInput = filterItem.querySelector("input[name*='[_destroy]']");

        const filterIdValue = parseInt(filterIdInput.value, 10) || null;
        const filterOperatorValue = filterOperatorInput.value;
        const filterValueValue = filterValueInput.value;
        const filterCaseSensitiveValue = filterCaseSensitiveInput.checked;
        const filterDestroyValue = filterDestroyInput.value === "1";

        if (filterDestroyValue && filterIdValue) {
          // Include destroyed existing filters with _destroy: true
          rule.plan_type_rule_filters.push({
            id: filterIdValue,
            operator: filterOperatorValue,
            value: filterValueValue,
            case_sensitive: filterCaseSensitiveValue,
            _destroy: true,
          });

          return;
        }

        // Skip filter without an operator or value
        if (filterOperatorValue.length === 0 || filterValueValue.trim().length === 0) {
          return;
        }

        const filter = {
          id: filterIdValue,
          operator: filterOperatorValue,
          value: filterValueValue,
          case_sensitive: filterCaseSensitiveValue,
          _destroy: filterDestroyValue,
        };

        if (filterIdInput && filterIdInput.value) {
          filter.id = parseInt(filterIdInput.value, 10);
        }

        rule.plan_type_rule_filters.push(filter);
      });

      rules.push(rule);
    });

    // Filter out rules that have no filters
    return rules.filter((rule) => rule.plan_type_rule_filters.length > 0);
  }

  #debouncePlansFetching() {
    if (this.updateUnmatchedPlansTimeout) {
      clearTimeout(this.updateUnmatchedPlansTimeout);
    }

    this.updateUnmatchedPlansTimeout = setTimeout(() => {
      this.#fetchUnmatchedPlans();
      this.#fetchMatchedPlans();
    }, 2000);
  }

  #fetchUnmatchedPlans() {
    const unmatchedPlansFrame = this.unmatchedPlansFrameTarget;

    // Show spinner in unmatched plans frame while loading
    if (unmatchedPlansFrame) {
      const spinnerTemplate = this.spinnerTemplateTarget.content.cloneNode(true);
      unmatchedPlansFrame.innerHTML = "";
      unmatchedPlansFrame.appendChild(spinnerTemplate);
    }

    const url = "/admin/unmatched_membership_plans/index";

    this.#loadPlans(url);
  }

  #fetchMatchedPlans() {
    const rules = this.#prepareRulesPayload().filter((rule) => !rule._destroy);

    if (rules.length === 0) {
      return;
    }

    rules.forEach((rule) => {
      const uiVirtualId = rule.id || rule.ui_virtual_id;

      const matchedPlansFrameId = `matched-plans-${uiVirtualId}`;
      const matchedPlansFrame = this.matchedPlansFrameTargets.find(
        (frame) => frame.id === matchedPlansFrameId
      );

      if (matchedPlansFrame) {
        const spinnerTemplate = this.spinnerTemplateTarget.content.cloneNode(true);
        matchedPlansFrame.innerHTML = "";
        matchedPlansFrame.appendChild(spinnerTemplate);
      }

      const url = `/admin/matched_membership_plans/index?rule_ui_virtual_id=${uiVirtualId}`;

      this.#loadPlans(url);
    });
  }

  async #loadPlans(url) {
    const response = await post(url, {
      body: JSON.stringify({
        rules_payload: JSON.stringify(this.planTypeRulesValue),
      }),
      contentType: "application/json",
      responseKind: "turbo-stream",
    });

    if (response.ok) {
      const text = await response.text;
      Turbo.renderStreamMessage(text);
    } else {
      console.error("Failed to load plans:", response.statusCode);
    }
  }

  #generateRandomString(length = 16) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";

    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }

    return result;
  }
}
